import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, zPhoneNumber } from '@harvestiq/zod';
import { phoneTypes } from 'common';
import { Button } from '@harvestiq/ui/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@harvestiq/ui/components/ui/form';
import { Input } from '@harvestiq/ui/components/ui/input';
import { useModal, UserFromContext, useToast, useUser } from '../../contexts';
import { useLegacyIIQApi } from '../../contexts/LegacyIIQApi/LegacyIIQApiContextProvider';
import { useUpdateUser } from '../../queries/users';
import { LegacySequelizeUser } from '@harvestiq/iiq-common';
import { InputMask, format } from '@react-input/mask';

const phoneMaskOptions = {
  mask: '___-___-____',
  replacement: { _: /\d/ },
};

const ssnMaskOptions = {
  mask: '___-__-____',
  replacement: { _: /\d/ },
};

const createCompleteProfileSchema = (isAgent: boolean) =>
  z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    phoneNumber: zPhoneNumber().transform((val) => val.replaceAll('-', '')),
    ssn: isAgent
      ? z
          .string()
          .min(1, 'SSN is required for agents')
          .transform((val) => val.replaceAll('-', ''))
      : z
          .string()
          .optional()
          .transform((val) => (val ? val.replaceAll('-', '') : undefined)),
  });

type CompleteProfileFormValues = z.infer<
  ReturnType<typeof createCompleteProfileSchema>
>;

type CompleteProfileModalProps = {
  user: UserFromContext;
};

const CompleteProfileModal = ({ user }: CompleteProfileModalProps) => {
  const { updateUserProfileByUserId } = useLegacyIIQApi();
  const { isUserProfileComplete, getMe } = useUser();
  const { closeModal } = useModal();
  const { errorToast, successToast } = useToast();
  const updateUserMutation = useUpdateUser();

  const completeProfileSchema = createCompleteProfileSchema(user.isAgent);

  const form = useForm<CompleteProfileFormValues>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.profile?.primaryPhone
        ? format(user.profile.primaryPhone, phoneMaskOptions)
        : '',
      ssn: user?.profile?.ssn ? format(user.profile.ssn, ssnMaskOptions) : '',
    },
  });

  useEffect(() => {
    if (isUserProfileComplete) {
      closeModal();
    }
  }, [isUserProfileComplete, closeModal]);

  const handleSubmit = async (values: CompleteProfileFormValues) => {
    const { errors: profileErrors } = await updateUserProfileByUserId(user.id, {
      ssn: values.ssn,
      phones: [
        {
          type: phoneTypes.mobile,
          isPrimary: true,
          number: values.phoneNumber,
        },
      ],
    });

    if (profileErrors) {
      console.error('Error updating profile information:', profileErrors);
      throw new Error('Failed to update profile information');
    }

    await updateUserMutation.mutateAsync(
      {
        userId: user.id,
        body: {
          firstName: values.firstName,
          lastName: values.lastName,
        },
      },
      {
        onSuccess: async () => {
          successToast('Profile successfully updated!');
          await getMe();
        },
        onError: () => {
          errorToast('Oops, something went wrong. Please try again.');
        },
      }
    );
  };

  return (
    <div className="modal p1 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">
          Complete your profile to get started
        </h2>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your first name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your last name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <InputMask
                    {...field}
                    {...phoneMaskOptions}
                    showMask
                    placeholder="Enter your phone number"
                    component={Input}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ssn"
            render={({ field }) => (
              <FormItem>
                <FormLabel>SSN</FormLabel>
                <FormControl>
                  <InputMask
                    {...field}
                    {...ssnMaskOptions}
                    showMask
                    placeholder="Enter your SSN"
                    component={Input}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              disabled={updateUserMutation.isPending}
              data-cy="user-profile-form-save-btn"
            >
              Save
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CompleteProfileModal;
